import React, { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PreviewIcon from '@mui/icons-material/Preview';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';

import { meterReadingsApi } from '../../services/api';
import MobileReadingsTab from './MobileReadingsTab';

const today = new Date().toISOString().slice(0, 10);

const parseFilename = (contentDisposition, fallback) => {
  const match = /filename="?([^";]+)"?/i.exec(contentDisposition || '');
  return match?.[1] || fallback;
};

const downloadBlob = (response, fallbackName) => {
  const filename = parseFilename(response.headers?.['content-disposition'], fallbackName);
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const actionColor = (action) => {
  if (action === 'create') return 'success';
  if (action === 'update') return 'info';
  if (action === 'blocked') return 'error';
  if (action === 'skip') return 'warning';
  return 'default';
};

const ReadingImportExportTab = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const now = new Date();

  const [exportOptions, setExportOptions] = useState({
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    format: 'importazioni',
    fileType: 'xlsx',
    prefix: 'CS',
    company: 'CENTRO SERVIZI',
    meterReaderCode: '',
    codiceTipologiaImpianto: '01',
  });
  const [file, setFile] = useState(null);
  const [defaultDate, setDefaultDate] = useState(today);
  const [mapping, setMapping] = useState({});
  const [previewData, setPreviewData] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const validRows = useMemo(() => previewData?.preview?.filter((row) => row.valid) || [], [previewData]);
  const allSelected = validRows.length > 0 && selectedRows.size === validRows.length;
  const someSelected = selectedRows.size > 0 && selectedRows.size < validRows.length;

  const exportMutation = useMutation({
    mutationFn: () => meterReadingsApi.exportCentroServizi(exportOptions),
    onSuccess: (response) => {
      downloadBlob(response, `letture-${exportOptions.year}-${String(exportOptions.month).padStart(2, '0')}.${exportOptions.fileType}`);
      setSnackbar({ open: true, severity: 'success', message: 'File letture esportato' });
    },
    onError: (err) => setSnackbar({ open: true, severity: 'error', message: err.response?.data?.message || 'Errore export letture' })
  });

  const previewMutation = useMutation({
    mutationFn: () => meterReadingsApi.previewCentroServiziImport(file, { defaultDate, mapping }),
    onSuccess: (response) => {
      const data = response.data?.data;
      setPreviewData(data);
      setSelectedRows(new Set((data?.preview || []).filter((row) => row.valid).map((row) => row.row)));
      setSnackbar({ open: true, severity: 'success', message: `Preview caricata: ${data?.valid_rows || 0} righe valide` });
    },
    onError: (err) => setSnackbar({ open: true, severity: 'error', message: err.response?.data?.message || 'Errore preview import' })
  });

  const executeMutation = useMutation({
    mutationFn: () => meterReadingsApi.executeCentroServiziImport(file, {
      defaultDate,
      mapping,
      selectedRows: Array.from(selectedRows),
    }),
    onSuccess: (response) => {
      const result = response.data?.data?.import_result;
      queryClient.invalidateQueries({ queryKey: ['meter-readings'] });
      queryClient.invalidateQueries({ queryKey: ['mobile-sessions'] });
      setSnackbar({
        open: true,
        severity: 'success',
        message: `Import completato: ${result?.created || 0} create, ${result?.updated || 0} aggiornate`,
      });
      setPreviewData(null);
      setSelectedRows(new Set());
    },
    onError: (err) => setSnackbar({ open: true, severity: 'error', message: err.response?.data?.message || 'Errore import letture' })
  });

  const updateExportOption = (field, value) => {
    setExportOptions((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (event) => {
    const selected = event.target.files?.[0] || null;
    setFile(selected);
    setPreviewData(null);
    setSelectedRows(new Set());
  };

  const toggleAllRows = (checked) => {
    setSelectedRows(checked ? new Set(validRows.map((row) => row.row)) : new Set());
  };

  const toggleRow = (rowNumber) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowNumber)) next.delete(rowNumber);
      else next.add(rowNumber);
      return next;
    });
  };

  const mappingValue = (field) => {
    if (mapping[field] !== undefined) return mapping[field];
    const key = field === 'meter_number' ? 'meterIndex' : field === 'lettura_mc' ? 'readingIndex' : field === 'data_lettura' ? 'dateIndex' : 'noteIndex';
    const value = previewData?.columnMap?.[key];
    return Number.isInteger(value) && value >= 0 ? value : '';
  };

  const renderMappingSelect = (field, label) => (
    <FormControl fullWidth size="small">
      <InputLabel>{label}</InputLabel>
      <Select
        value={mappingValue(field)}
        label={label}
        onChange={(event) => setMapping((prev) => ({ ...prev, [field]: event.target.value }))}
      >
        <MenuItem value="">Non usare</MenuItem>
        {(previewData?.headers || []).map((header, index) => (
          <MenuItem key={`${field}-${index}`} value={index}>{index + 1}. {header || '(vuota)'}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} lg={5}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <DownloadIcon color="primary" />
              <Typography variant="h6">Export file letture</Typography>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Anno" type="number" value={exportOptions.year} onChange={(e) => updateExportOption('year', e.target.value)} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Mese" type="number" inputProps={{ min: 1, max: 12 }} value={exportOptions.month} onChange={(e) => updateExportOption('month', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Formato</InputLabel>
                  <Select value={exportOptions.format} label="Formato" onChange={(e) => updateExportOption('format', e.target.value)}>
                    <MenuItem value="importazioni">Importazioni SAP</MenuItem>
                    <MenuItem value="centro-servizi">Centro Servizi</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>File</InputLabel>
                  <Select value={exportOptions.fileType} label="File" onChange={(e) => updateExportOption('fileType', e.target.value)}>
                    <MenuItem value="xlsx">Excel XLSX</MenuItem>
                    <MenuItem value="csv">CSV</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Prefisso ODL" value={exportOptions.prefix} onChange={(e) => updateExportOption('prefix', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Codice tipologia" value={exportOptions.codiceTipologiaImpianto} onChange={(e) => updateExportOption('codiceTipologiaImpianto', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Società" value={exportOptions.company} onChange={(e) => updateExportOption('company', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Letturista codice" value={exportOptions.meterReaderCode} onChange={(e) => updateExportOption('meterReaderCode', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <Button fullWidth variant="contained" startIcon={<DownloadIcon />} onClick={() => exportMutation.mutate()} disabled={exportMutation.isPending}>
                  {exportMutation.isPending ? 'Esportazione...' : 'Scarica file letture'}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={7}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <UploadFileIcon color="primary" />
              <Typography variant="h6">Import file compilato</Typography>
            </Stack>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <Button fullWidth variant="outlined" component="label" startIcon={<UploadFileIcon />} sx={{ py: 1.25 }}>
                  {file ? file.name : 'Seleziona file Excel'}
                  <input hidden type="file" accept=".xlsx,.xls,.csv" onChange={handleFileChange} />
                </Button>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField fullWidth size="small" label="Data default" type="date" value={defaultDate} onChange={(e) => setDefaultDate(e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button fullWidth variant="contained" startIcon={<PreviewIcon />} onClick={() => previewMutation.mutate()} disabled={!file || previewMutation.isPending}>
                  {previewMutation.isPending ? 'Analisi...' : 'Analizza file'}
                </Button>
              </Grid>
            </Grid>

            {previewMutation.isPending && <LinearProgress sx={{ mt: 2 }} />}

            {previewData && (
              <Box sx={{ mt: 2 }}>
                <Alert severity={previewData.valid_rows > 0 ? 'info' : 'warning'} sx={{ mb: 2 }}>
                  {previewData.valid_rows} righe valide su {previewData.total_rows}. Nuove: {previewData.stats?.create || 0}, aggiornamenti: {previewData.stats?.update || 0}, bloccate: {previewData.stats?.blocked || 0}, saltate: {previewData.stats?.skipped || 0}, anomalie: {previewData.stats?.anomalies || 0}{previewData.ignored_rows ? `, vuote ignorate: ${previewData.ignored_rows}` : ''}.
                </Alert>

                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6} md={3}>{renderMappingSelect('meter_number', 'Matricola')}</Grid>
                  <Grid item xs={12} sm={6} md={3}>{renderMappingSelect('lettura_mc', 'Lettura')}</Grid>
                  <Grid item xs={12} sm={6} md={3}>{renderMappingSelect('data_lettura', 'Data')}</Grid>
                  <Grid item xs={12} sm={6} md={3}>{renderMappingSelect('note', 'Note')}</Grid>
                  <Grid item xs={12}>
                    <Button size="small" variant="outlined" onClick={() => previewMutation.mutate()} disabled={!file || previewMutation.isPending}>
                      Ricalcola preview con mapping
                    </Button>
                  </Grid>
                </Grid>

                <TableContainer sx={{ maxHeight: isMobile ? 360 : 420 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox checked={allSelected} indeterminate={someSelected} onChange={(e) => toggleAllRows(e.target.checked)} />
                        </TableCell>
                        <TableCell>Riga</TableCell>
                        <TableCell>Contatore</TableCell>
                        <TableCell align="right">Lettura</TableCell>
                        <TableCell>Data</TableCell>
                        <TableCell>Azione</TableCell>
                        <TableCell>Esito</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(previewData.preview || []).slice(0, 200).map((row) => (
                        <TableRow key={row.row} hover selected={selectedRows.has(row.row)}>
                          <TableCell padding="checkbox">
                            <Checkbox checked={selectedRows.has(row.row)} disabled={!row.valid} onChange={() => toggleRow(row.row)} />
                          </TableCell>
                          <TableCell>{row.row}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">{row.meter_number || '-'}</Typography>
                            {row.meter_id && <Typography variant="caption" color="text.secondary">ID {row.meter_id}</Typography>}
                          </TableCell>
                          <TableCell align="right">{row.lettura_mc ?? '-'}</TableCell>
                          <TableCell>{row.data_lettura || '-'}</TableCell>
                          <TableCell><Chip size="small" label={row.action} color={actionColor(row.action)} variant="outlined" /></TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                              {row.valid ? <CheckCircleIcon color="success" fontSize="small" /> : <ErrorIcon color="error" fontSize="small" />}
                              {row.warnings?.length > 0 && <Tooltip title={row.warnings.join(' | ')}><WarningIcon color="warning" fontSize="small" /></Tooltip>}
                              <Typography variant="caption" color={row.valid ? 'text.secondary' : 'error'}>
                                {row.errors?.length ? row.errors.join(' | ') : row.warnings?.join(' | ') || 'OK'}
                              </Typography>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {selectedRows.size} righe selezionate per l'import
                  </Typography>
                  <Button variant="contained" color="success" startIcon={<UploadFileIcon />} onClick={() => executeMutation.mutate()} disabled={!file || selectedRows.size === 0 || executeMutation.isPending}>
                    {executeMutation.isPending ? 'Importazione...' : 'Importa letture selezionate'}
                  </Button>
                </Stack>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" sx={{ mb: 2 }}>Sessioni importate e rilevazioni</Typography>
      <MobileReadingsTab />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReadingImportExportTab;